// User roles
export type UserRole = 'counselor' | 'admin' | 'leader';

// Issue types
export type IssueType = 'spiritual' | 'relational' | 'personal';

// User interface
export interface User {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  isActive: boolean;
  avatarUrl?: string;
  createdAt: Date;
  lastLogin?: Date;
  deactivatedAt?: Date;
  /** Set after the first-run counselor tutorial is dismissed or completed. */
  tutorialCompletedAt?: Date;
  /** Unique privileged leader; stored in Firestore, not hardcoded in source. */
  isSupremeLeader?: boolean;
  /** Marked as a mentor who can take apprentices on their own cases. */
  isMentor?: boolean;
  /**
   * Push registrations, one per device. Read this rather than `fcmTokens`: it already folds in
   * any token that exists only in the legacy flat list.
   */
  fcmDevices?: FcmDevice[];
  /**
   * Flat mirror of the tokens in `fcmDevices`, written for Cloud Functions and for clients still
   * on the old build. Kept in sync by `pushNotifications`; carries no provenance of its own.
   */
  fcmTokens?: string[];
}

/**
 * One push registration, labelled so the token can be traced back to a device. This is the
 * app-level shape: Firestore stores `lastSeenAt` as a `Timestamp`, so build these through
 * `fcmDevicesFromUserData` rather than casting a document straight to this type.
 */
export interface FcmDevice {
  token: string;
  /** Human-readable, e.g. "iPhone · aplicație instalată". */
  label: string;
  /** Last time this device opened the app and confirmed its token. */
  lastSeenAt: Date;
}

/**
 * Guided case description. Four questions asked in the order a counselor
 * actually learns the answers; every one optional except `whyCame`, because a
 * field filled in to satisfy a validator is worse than an empty one.
 */
export interface CaseProfileAnswers {
  whyCame: string;
  currentSituation: string;
  background: string;
  hopes: string;
}

export type CaseProfileFieldKey = keyof CaseProfileAnswers;

export interface CaseProfile extends CaseProfileAnswers {
  updatedAt: Date;
  updatedBy: string;
  updatedByName: string;
}

/** Mentorship lifecycle: an apprentice shadows a mentor on specific cases. */
export type MentorshipStatus = 'active' | 'completed';

/**
 * One mentor -> one apprentice pairing, plus the mentor cases the apprentice
 * may observe. Completed pairings are kept, not deleted: the apprentice keeps
 * read-only access and the notes they wrote stay on the case.
 */
export interface Mentorship {
  id: string;
  mentorUserId: string;
  mentorName: string;
  apprenticeUserId: string;
  apprenticeName: string;
  status: MentorshipStatus;
  /** Cases of the mentor this apprentice may open. */
  caseIds: string[];
  startedAt: Date;
  completedAt?: Date | null;
  /** Supreme leader who set the pairing up. */
  createdBy: string;
  createdByName: string;
  updatedAt: Date;
}

// Counselor interface
export interface Counselor {
  id: string;
  fullName: string;
  /** Prenume */
  firstName?: string;
  /** Nume de familie */
  lastName?: string;
  email: string;
  phoneNumber: string;
  /** Used for propose-by-default when matching counselee sex */
  sex?: Sex;
  birthDate?: Date | null;
  specialties: string[]; // What kind of problems they're good at
  /** Custom specialty name → issue category (COMMON_SPECIALTIES use static map) */
  specialtyCategories?: Record<string, IssueType>;
  activeCases: number;
  workloadLevel: 'low' | 'moderate' | 'high';
  linkedUserId?: string; // Link to user account from admin tools
  avatarUrl?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Case status
export type CaseStatus = 'waiting' | 'active' | 'unfinished' | 'finished' | 'cancelled';
export type CaseClosureStatus = Extract<
  CaseStatus,
  'unfinished' | 'finished' | 'cancelled'
>;

/** Structured last-session assessment, stored as an enum for later stats. */
export type CaseFinalEvaluation =
  | 'continue_alone'
  | 'needs_follow_up'
  | 'continue_counseling'
  | 'other_responsible';

export const CASE_FINAL_EVALUATIONS: CaseFinalEvaluation[] = [
  'continue_alone',
  'needs_follow_up',
  'continue_counseling',
  'other_responsible',
];

export interface CaseClosure {
  status: CaseClosureStatus;
  closedAt: Date;
  /** Optional 0-based road session associated with the closure. */
  sessionNumber: number | null;
  problemUnderstanding: string;
  observedChanges: string;
  gospelRelationship: string;
  finalEvaluation: CaseFinalEvaluation | null;
  evaluationDetails: string;
  createdBy: string;
  createdByName: string;
  /** Derived from finalEvaluation for cheap later filters. */
  followUpRequired: boolean;
  unfinishedReason: string;
  nextSteps: string;
  cancellationReason: string;
}

/** Assignment proposal lifecycle for intake workflow */
export type AssignmentStatus = 'none' | 'pending' | 'accepted' | 'forced';

// Civil status
export type CivilStatus = 'unmarried' | 'married' | 'divorced' | 'engaged' | 'widowed';

// Sex/Gender
export type Sex = 'masculin' | 'feminin';

/** How the counselee was referred (optional intake) */
export type ReferralSource = 'pastor' | 'self' | 'friend' | 'other';

/** Intake priority for triage visibility */
export type CasePriority = 'normal' | 'high';

/** Meeting cadence in weeks: 1=weekly, 2=biweekly, 3=every 3 weeks, 4=monthly */
export type MeetingFrequencyWeeks = 1 | 2 | 3 | 4;

/** Structured next-step / homework type on a session report */
export type NextStepType =
  | 'scripture'
  | 'prayer'
  | 'practice'
  | 'conversation'
  | 'reconciliation'
  | 'reflection'
  | 'other';

export const NEXT_STEP_TYPES: NextStepType[] = [
  'scripture',
  'prayer',
  'practice',
  'conversation',
  'reconciliation',
  'reflection',
  'other',
];

/** Denormalized open homework on a case (from latest report with commitments). */
export interface CaseOpenNextStep {
  type: NextStepType;
  description: string;
  passage: string | null;
  /**
   * Road session index (0-based), not the stored `sessionNumber`. Legacy cases start at 1 in
   * Firestore, so writing the raw value here made the card disagree with every other label.
   * Null on steps saved before the field existed — the session is unknown, not session 0.
   */
  fromSessionNumber: number | null;
}

// Case interface
export interface Case {
  id: string;
  title: string;
  /** Display name (Prenume Nume); kept for legacy reads/UI */
  counseledName: string;
  /** Prenume */
  firstName?: string;
  /** Nume de familie */
  lastName?: string;
  age: number;
  sex?: Sex; // Optional for backward compatibility
  civilStatus: CivilStatus;
  issueTypes: IssueType[];
  phoneNumber: string;
  description: string;
  /** Optional: pastor / self / friend / other */
  referralSource?: ReferralSource | null;
  /** Defaults to normal when missing (legacy cases) */
  priority?: CasePriority;
  status: CaseStatus;
  assignedCounselorId?: string;
  assignedCounselorName?: string;
  assignmentStatus?: AssignmentStatus;
  proposedCounselorId?: string | null;
  proposedCounselorName?: string | null;
  /** User who proposed the counselor (leader/admin) — used for accept/refuse notifications */
  proposedByUserId?: string | null;
  proposedByUserName?: string | null;
  meetingFeedback?: string; // Notes from counseling sessions
  /** Agreed meeting cadence in weeks (1=weekly, 2=biweekly, 3=every 3 weeks, 4=monthly) */
  meetingFrequencyWeeks?: MeetingFrequencyWeeks | null;
  /** Date of the last recorded session meeting (from session reports) */
  lastMeetingDate?: Date | null;
  /**
   * Open homework from the latest session report (denormalized for briefing/dashboard).
   * Null when the last report had no next-step commitments.
   */
  openNextStep?: CaseOpenNextStep | null;
  /** Main theme of the latest session report (denormalized). */
  lastTheme?: string | null;
  /**
   * Session number still awaiting the post-session clarification (memento/tablou), set when the
   * counselor picks "Mai târziu" on the session-closed prompt. Cleared the moment the next
   * report is filed — that session's window for reflection has passed by then regardless of
   * whether a note was ever written, so writing one is deliberately not what clears this.
   */
  pendingClarifySession?: number | null;
  /** Latest structured closure record; retained if the case is reopened. */
  closure?: CaseClosure | null;
  /** Guided description. `description` stays as its derived plain-text form. */
  profile?: CaseProfile | null;
  /** True when a counseling consent file is attached (Phase 2+ writes this) */
  consentAttached?: boolean;
  consentFileName?: string | null;
  consentContentType?: string | null;
  consentUploadedAt?: Date | null;
  consentUploadedByName?: string | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // User ID who created the case
}

// Session report interface
export interface SessionReport {
  id: string;
  caseId: string;
  counselorId: string;
  counselorName: string;
  date: Date;
  notes: string;
  progress: string;
  nextSteps: string;
  prayerRequests?: string;
  createdAt: Date;
}

/**
 * The four-layer guided reflection (SITUAȚIA → RĂSPUNSURILE → CREDINȚELE → MOTIVELE, Tripp's
 * model), filled in against one specific session. Every field is optional — the counselor
 * writes what's there and leaves the rest. The self-check questions live in the memento
 * ("Întrebări ajutătoare") instead, as prompts with no answer field of their own.
 */
export interface MeetingNoteTablou {
  situatia?: string;
  raspunsurile?: string;
  gandurile?: string;
  motivele?: string;
}

/** Private meeting note for a counseling case (optionally tied to a session). */
export interface MeetingNote {
  id: string;
  caseId: string;
  content: string;
  /** Road session index (0-based). Null for legacy notes without a session. */
  sessionNumber: number | null;
  createdAt: Date;
  createdBy: string;
  createdByName: string;
  updatedAt: Date;
  /**
   * Present when this note is a guided tablou rather than a freeform note. `content` is still
   * kept in sync (the layers rendered as readable text) so every existing note list, preview and
   * timeline shows it without special-casing.
   */
  tablou?: MeetingNoteTablou | null;
}

// Appointment interface
export interface Appointment {
  id: string;
  title: string;
  description?: string;
  /** Optional 0-based session number from the case session road. */
  sessionNumber?: number | null;
  date: Date;
  startTime: string;
  endTime: string;
  counselorId: string;
  counselorName: string;
  caseId?: string; // Optional link to a case
  caseTitle?: string;
  room?: string; // Church room where counseling will take place
  createdBy: string;
  createdAt: Date;
}

// Church event (Eveniment) — separate from counseling appointments
export interface ChurchEvent {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  startTime: string;
  endTime: string;
  registrationUrl?: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

/** Marital status for monthly accountability report */
export type MonthlyReportMaritalStatus = 'casatorit' | 'necasatorit';

/** Answers for the monthly dare-de-socoteală form */
export interface MonthlyReportAnswers {
  relationshipWithGod: string;
  mostAliveDiscipline: string;
  disciplineNeedsStrengthening: string;
  maritalStatus: MonthlyReportMaritalStatus;
  marriageFamilyNotes: string;
  closeRelationshipsNotes: string;
  needsPersonalRelationshipSupport: string;
  heartState: string;
  feelsTiredOrBurdened: string;
  howLeaderOrTeamCanHelp: string;
  departmentImprovements: string;
}

/** Monthly accountability report (Raport lunar) */
export interface MonthlyReport {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  monthKey: string; // YYYY-MM of the reported month
  answers: MonthlyReportAnswers;
  submittedAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

// Dashboard metrics
export interface DashboardMetrics {
  totalCases: number;
  casesByStatus: {
    waiting: number;
    active: number;
    unfinished: number;
    finished: number;
  };
  totalCounselors: number;
  counselorWorkload: {
    low: number;
    moderate: number;
    high: number;
  };
  upcomingAppointments: number;
}

/** External resource link kind (Google Docs/Sheets/Slides/PDF/audio/video/other) */
export type ResourceLinkKind =
  | 'doc'
  | 'sheet'
  | 'powerpoint'
  | 'pdf'
  | 'audio'
  | 'video'
  | 'other';

/** Preset accent color for a resource folder icon */
export type ResourceFolderColor =
  | 'brand'
  | 'slate'
  | 'sky'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'violet'
  | 'orange';

/** Folder in the department resources tree */
export interface ResourceFolder {
  id: string;
  name: string;
  parentId: string | null;
  allowAdmins: boolean;
  allowCounselors: boolean;
  color: ResourceFolderColor;
  sortOrder: number;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

/** External URL link inside a resource folder */
export interface ResourceLink {
  id: string;
  folderId: string;
  title: string;
  url: string;
  kind: ResourceLinkKind;
  sortOrder: number;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

